import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import propertyService from "../../Services/PropertyService";
import { PropertyTypeList } from "../../../Enum";

const pageStyles = [
  {
    id: "luxurious",
    label: "Luxurious and Rich",
    color:
      "text-yellow-600 bg-gradient-to-r from-custom-yellow1 via-custom-yellow2 to-custom-yellow3 text-transparent bg-clip-text",
    available: true,
  },
  {
    id: "urban",
    label: "Urban and Modern",
    color:
      "text-green-600 bg-gradient-to-r from-custom-green1 to-custom-green2 text-transparent bg-clip-text",
    available: true,
  },
  {
    id: "vibrant",
    label: "Vibrant and Youthful",
    color:
      "bg-gradient-to-r from-custom-purple1 to-custom-purple2 text-transparent bg-clip-text",
    available: false,
  },
  {
    id: "warm",
    label: "Warm and Family-Friendly",
    color:
      "text-orange-600 bg-gradient-to-r from-custom-orange1 to-custom-orange2 text-transparent bg-clip-text",
    available: false,
  },
  {
    id: "industrial",
    label: "Industrial and Professional",
    color:
      "text-blue-600 bg-gradient-to-r from-custom-blue1 to-custom-blue2 text-transparent bg-clip-text",
    available: false,
  },
];

const GLpageTemplatePopup = ({
  ClosePopup,
  GLPageId,
  initialProperty,
  initialStyle,
  initialPropertyType,
  initialPropertyId,
}) => {
  const navigate = useNavigate();

  const [selectedStyle, setSelectedStyle] = useState(initialStyle || {});
  const [selectedProperty, setSelectedProperty] = useState(initialProperty || "");
  const [selectedPropertyId, setSelectedPropertyId] = useState(initialPropertyId || "");
  const [selectedPropertyTypeId, setSelectedPropertyTypeId] = useState(initialPropertyType?.trim() || "");
  const [selectedPropertyTypeName , setSelectedPropertyTypeName] = useState("");

  const [propertyData, setPropertyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // console.log("selectedPropertyTypeName" , selectedPropertyTypeName)

  useEffect(() => {
    const fetchPropertyName = async () => {
      if (!selectedPropertyTypeId) return;

      setIsLoading(true);
      try {
        const selectedType = PropertyTypeList.find((type) => type.id === selectedPropertyTypeId);
        const response = await propertyService.getPropertiesNameType(selectedType?.label);

        setPropertyData(response.data || []);
        setErrorMsg("");

      
        if (initialPropertyId) {
          const foundProperty = response.data?.find((item) => item.id === initialPropertyId);
          if (foundProperty) {
            setSelectedProperty(foundProperty.propertyName);
          }
        }

      } catch (error) {
        setErrorMsg("Failed to load properties. Please try again.");
        setPropertyData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPropertyName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyTypeId]);

  const handleProceed = () => {
    if (!selectedStyle.label || !selectedProperty || !selectedPropertyId) {
      setErrorMsg("Please select both a property and a page style.");
      return;
    }

    const selectedStyleDetails = {
      label: selectedStyle.label,
      color: selectedStyle.color,
    };

    if (GLPageId) {
      ClosePopup({
        selectedStyleDetails,
        selectedProperty,
        selectedPropertyId,
        selectedPropertyTypeId,
      });
    } else {
   
      navigate("/admin/gl-pages-creating", {
        state: {
          selectedStyleDetails,
          selectedProperty,
          selectedPropertyTypeName ,
          selectedPropertyId,
          selectedPropertyTypeId,
        },
      });
      ClosePopup();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl max-h-[700px] overflow-y-auto animate-fadeIn">

        {/* Header */}
        <div className="flex justify-between items-center mb-6 border-b pb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {GLPageId ? "Update Property & Style" : "Select Property & Style"}
          </h2>
          <button
            onClick={() => ClosePopup()}
            className="text-gray-400 hover:text-gray-800 transition-colors"
            aria-label="Close popup"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Property Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Select Property</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Property Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
              <select
                className="w-full shadow border-2 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                value={selectedPropertyTypeId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedType = PropertyTypeList.find(type => type.id === selectedId);
                
                  setSelectedPropertyTypeId(selectedId);
                  if (selectedType) {
                    setSelectedPropertyTypeName(selectedType.label); 
                  }
                
                  setSelectedPropertyId("");
                  setSelectedProperty("");
                }}
                
              >
                <option value="">Select property type</option>
                {PropertyTypeList.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Property Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Name</label>
              <select
                className="w-full shadow border-2 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-gray-100 disabled:text-gray-500"
                value={selectedPropertyId}
                disabled={!propertyData.length || isLoading}
                onChange={(e) => {
                  const selected = propertyData.find(item => item.id === e.target.value);
                  setSelectedPropertyId(selected?.id || "");
                  setSelectedProperty(selected?.propertyName || "");
                }}
              >
                <option value="">{isLoading ? "Loading properties..." : "Select property"}</option>
                {propertyData.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                    disabled={!item.status}
                    title={!item.status ? "Property template already exists" : ""}
                  >
                    {item.propertyName || "Unnamed Property"}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Select Page Style</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pageStyles.map((style) => {
              const isSelected = selectedStyle.label === style.label;
              return (
                <div
                  key={style.id}
                  className={`border-2 p-4 rounded-lg flex items-center justify-between relative cursor-pointer transition ${
                    isSelected
                      ? "border-emerald-600 bg-emerald-50"
                      : "border-gray-200 hover:border-gray-300 hover:shadow"
                  } ${!style.available ? "opacity-60 cursor-not-allowed" : ""}`}
                  onClick={() => style.available && setSelectedStyle(style)}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="pageStyle"
                      id={style.id}
                      checked={isSelected}
                      onChange={() => style.available && setSelectedStyle(style)}
                      disabled={!style.available}
                      className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <label
                      htmlFor={style.label}
                      className={`font-semibold text-base sm:text-lg ${style.color}`}
                    >
                      {style.label}
                    </label>
                  </div>
                  {!style.available && (
                    <span className="bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded">
            <p className="text-red-700 text-sm">{errorMsg}</p>
          </div>
        )}

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            onClick={() => ClosePopup()}
          >
            Cancel
          </button>
          <button
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            onClick={handleProceed}
            disabled={!selectedPropertyId || !selectedStyle?.label}
          >
            {GLPageId ? "Update" : "Proceed"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default GLpageTemplatePopup;
